import JSZip from "jszip";
import { saveAs } from "file-saver";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import AppHeader from "./components/AppHeader";
import { apiUrl } from "./api";
import { supabase } from "./lib/supabase";

// code for qti converter
const outputFileName = "accepted_questions";

const escapeXml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const generateQtiXml = (questions) => {
  let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<questestinterop xmlns="http://www.imsglobal.org/xsd/ims_qtiasiv1p2">`;

  for (const q of questions) {
    xml += `\n  <item title="Question ${q.id}" ident="q${q.id}">
    <presentation>
      <material><mattext>${escapeXml(q.question)}</mattext></material>
      <response_lid ident="response1" rcardinality="Single">
        <render_choice>`;

    for (const [ident, text] of Object.entries(q.options)) {
      xml += `\n          <response_label ident="${ident}"><material><mattext>${escapeXml(
        text
      )}</mattext></material></response_label>`;
    }

    xml += `\n        </render_choice>
      </response_lid>
    </presentation>
    <resprocessing>
      <respcondition continue="No">
        <conditionvar>
          <varequal respident="response1">${q.answer}</varequal>
        </conditionvar>
        <setvar action="Set">100</setvar>
      </respcondition>
    </resprocessing>
  </item>`;
  }

  xml += `\n</questestinterop>`;
  return xml;
};

const generateManifest = (qtiFileName, questionBankName = "DeepToast Question Bank") => {
  return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="man1"
  xmlns="http://www.imsglobal.org/xsd/imscp_v1p1"
  xmlns:imsmd="http://www.imsglobal.org/xsd/imsmd_v1p2"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imscp_v1p1
    http://www.imsglobal.org/xsd/imscp_v1p1.xsd
    http://www.imsglobal.org/xsd/imsmd_v1p2
    http://www.imsglobal.org/xsd/imsmd_v1p2p2.xsd">
  <metadata>
    <schema>IMS Content</schema>
    <schemaversion>1.1.3</schemaversion>
    <imsmd:lom>
      <imsmd:general>
        <imsmd:title>
          <imsmd:langstring xml:lang="en">${escapeXml(questionBankName)}</imsmd:langstring>
        </imsmd:title>
        <imsmd:identifier>
          <imsmd:catalog>URI</imsmd:catalog>
          <imsmd:entry>${escapeXml(questionBankName)}</imsmd:entry>
        </imsmd:identifier>
      </imsmd:general>
    </imsmd:lom>
  </metadata>
  <organizations/>
  <resources>
    <resource identifier="res1" type="imsqti_xmlv1p2" href="${qtiFileName}">
      <metadata>
        <imsmd:lom>
          <imsmd:general>
            <imsmd:title>
              <imsmd:langstring xml:lang="en">${escapeXml(questionBankName)}</imsmd:langstring>
            </imsmd:title>
          </imsmd:general>
        </imsmd:lom>
      </metadata>
      <file href="${qtiFileName}"/>
    </resource>
  </resources>
</manifest>`;
};

// Try to map whatever correctAnswer is to a letter ident (A, B, C...)
const getAnswerIdent = (qOptions, correctAnswer) => {
  if (!qOptions) return "";

  const optionArray = Array.isArray(qOptions)
    ? qOptions
    : Object.values(qOptions);

  // Case 1: already a letter
  if (
    typeof correctAnswer === "string" &&
    /^[A-Da-d]$/.test(correctAnswer.trim())
  ) {
    return correctAnswer.trim().toUpperCase();
  }

  // Case 2: numeric index
  if (
    typeof correctAnswer === "number" &&
    correctAnswer >= 0 &&
    correctAnswer < optionArray.length
  ) {
    return String.fromCharCode(65 + correctAnswer); // 0 -> A, 1 -> B...
  }

  // Case 3: match by text value
  if (typeof correctAnswer === "string") {
    const idx = optionArray.findIndex(
      (opt) => String(opt).trim() === correctAnswer.trim()
    );
    if (idx !== -1) {
      return String.fromCharCode(65 + idx);
    }
  }

  return ""; // fallback
};

//actual code for generate quesiton page
const GenerateQuestion = () => {
  const [questions, setQuestions] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [editedOptions, setEditedOptions] = useState([]);
  const [metadataMap, setMetadataMap] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [numQuestions, setNumQuestions] = useState("");

  const [showEmptyExportModal, setShowEmptyExportModal] = useState(false);
  const [showConfirmExportModal, setShowConfirmExportModal] = useState(false);
  const [showFileTypeModal, setShowFileTypeModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAcceptAllModal, setShowAcceptAllModal] = useState(false);
  const [canvasCourseId, setCanvasCourseId] = useState("");
  const [canvasCourseName, setCanvasCourseName] = useState("");
  const [canvasQuestionBankName, setCanvasQuestionBankName] = useState("DeepToast Question Bank");
  const [canvasUploading, setCanvasUploading] = useState(false);
  const [canvasUploadError, setCanvasUploadError] = useState("");
  const [canvasUploadSuccess, setCanvasUploadSuccess] = useState(false);
  const [selectedFileTypes, setSelectedFileTypes] = useState({
    pdfTest: false,
    pdfAnswerKey: false,
    qti: false
  });
  const [generationWarnings, setGenerationWarnings] = useState([]);

  // Add state for course/module/params (for demo, hardcode or get from localStorage)
  const [selectedCourse, setSelectedCourse] = useState('IBUS620-Sandbox');
  const [selectedModule, setSelectedModule] = useState('Mod. 1 Intro');
  const [mcqPercentage, setMcqPercentage] = useState(60);
  const [tfPercentage, setTfPercentage] = useState(40);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const parseQuestions = (text) => {
    const blocks = text.split(/\n(?=\d+\.\s)/);
    const parsed = [];

    blocks.forEach((block) => {
      const trimmed = block.trim();
      if (!trimmed) return;

      const lines = trimmed.split("\n").map((l) => l.trim());
      if (!/^\d+\.\s/.test(lines[0])) return;

      let correctAnswer = null;
      for (let line of lines) {
        const match = line.match(/^Correct Answer:\s*(.*)$/i);
        if (match) {
          correctAnswer = match[1].trim();
          break;
        }
      }

      const contentLines = lines.filter((line) => !/^Rationale:/i.test(line));
      if (contentLines.length === 0) return;

      let questionText = contentLines[0]
        .replace(/^\d+\.\s*/, "")
        .replace(/True or False:\s*/i, "");

      const options = contentLines
        .slice(1)
        .filter((line) => /^[A-D]\)/.test(line) || /^[A-B]\)/.test(line))
        .map((line) => line.trim());

      const type = options.length > 0 ? "mcq" : "tf";

      parsed.push({
        type,
        question: questionText,
        options,
        correctAnswer,
      });
    });

    setQuestions(parsed);
  };

  // Remove regenerateQuestions and its button
  // Remove regenerateQuestions function definition

  useEffect(() => {
    const generatedQuestions = localStorage.getItem('generatedQuestions');
    if (generatedQuestions) {
      try {
        const questions = JSON.parse(generatedQuestions);
        setQuestions(questions);
      } catch (error) {
        console.error('Error parsing generated questions:', error);
        setQuestions([]);
      }
    } else {
      setQuestions([]);
    }

    const warningsRaw = localStorage.getItem('generationWarnings');
    if (warningsRaw) {
      try {
        const arr = JSON.parse(warningsRaw);
        if (Array.isArray(arr)) setGenerationWarnings(arr);
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    const savedData = localStorage.getItem("formData");
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        setNumQuestions(data.numQuestions || "");
        if (data.course) {
          setCanvasCourseId(String(data.course));
        }
        if (data.courseDisplayName) {
          setCanvasCourseName(String(data.courseDisplayName));
        }
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedText(questions[index].question);
    
    // Store options as objects with letter and text separately for editing
    const q = questions[index];
    let optionsArray = [];
    
    if (Array.isArray(q.options)) {
      // Convert array format to letter-text pairs
      optionsArray = q.options.map((opt, optIdx) => {
        if (typeof opt === 'string') {
          const match = opt.match(/^([A-D])\)\s*(.+)$/);
          if (match) {
            return { letter: match[1], text: match[2] };
          }
          // Fallback: assign letter based on index
          const letter = String.fromCharCode(65 + optIdx);
          return { letter, text: opt };
        }
        const letter = String.fromCharCode(65 + optIdx);
        return { letter, text: String(opt) };
      });
    } else if (q.options && typeof q.options === 'object') {
      // Convert object format to letter-text pairs
      optionsArray = Object.entries(q.options)
        .sort(([a], [b]) => a.localeCompare(b)) // Sort A, B, C, D
        .map(([key, value]) => ({ letter: key, text: value }));
    } else if (q.type === 'tf') {
      // For True/False questions, default options
      optionsArray = [
        { letter: 'A', text: 'True' },
        { letter: 'B', text: 'False' }
      ];
    }
    
    setEditedOptions(optionsArray);
  };

  const handleOptionChange = (idx, value) => {
    const newOptions = [...editedOptions];
    // Only update the text, keep the letter fixed
    newOptions[idx] = { ...newOptions[idx], text: value };
    setEditedOptions(newOptions);
  };

  const handleSave = () => {
    const updated = [...questions];
    updated[editingIndex].question = editedText.trim();
    
    // Convert letter-text pairs back to object format for consistency with backend
    const optionsObject = {};
    editedOptions.forEach((opt) => {
      if (opt && opt.letter && opt.text !== undefined) {
        optionsObject[opt.letter] = opt.text.trim();
      }
    });
    
    updated[editingIndex].options = optionsObject;
    setQuestions(updated);

    setMetadataMap((prev) => ({
      ...prev,
      [editingIndex]: {
        ...(prev[editingIndex] || {}),
        mode: "edited",
      },
    }));

    setEditingIndex(null);
    setEditedText("");
    setEditedOptions([]);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedText("");
    setEditedOptions([]);
  };

  const handleStatusChange = (index, status) => {
    setMetadataMap((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || { mode: "as-is" }),
        status,
      },
    }));
  };

  const handleAcceptAll = () => {
    const newMetadataMap = {};
    questions.forEach((_, index) => {
      newMetadataMap[index] = {
        ...(metadataMap[index] || { mode: "as-is" }),
        status: "accepted",
      };
    });
    setMetadataMap(newMetadataMap);
    setShowAcceptAllModal(false);
  };

  const handleAcceptAllClick = () => {
    setShowAcceptAllModal(true);
  };

  const handleCorrectAnswerChange = (questionIndex, newAnswerLetter) => {
    const updated = [...questions];
    const question = { ...questions[questionIndex] };
    
    // Get current correct answer letter
    const correctAnswerValue = question.correctAnswer !== undefined ? question.correctAnswer : question.answer;
    const currentCorrectLetter = getAnswerIdent(question.options, correctAnswerValue);
    
    // Swap option text values between old and new correct answer
    if (question.options && currentCorrectLetter && currentCorrectLetter !== newAnswerLetter) {
      if (Array.isArray(question.options)) {
        // For array format, swap the text parts
        const currentIdx = currentCorrectLetter.charCodeAt(0) - 65;
        const newIdx = newAnswerLetter.charCodeAt(0) - 65;
        if (currentIdx >= 0 && currentIdx < question.options.length && 
            newIdx >= 0 && newIdx < question.options.length) {
          const newOptions = [...question.options];
          // Extract text from option strings (they might have "A) Text" format)
          const currentText = newOptions[currentIdx].replace(/^[A-D]\)\s*/i, '').trim();
          const newText = newOptions[newIdx].replace(/^[A-D]\)\s*/i, '').trim();
          // Swap the text parts
          newOptions[currentIdx] = `${currentCorrectLetter}) ${newText}`;
          newOptions[newIdx] = `${newAnswerLetter}) ${currentText}`;
          question.options = newOptions;
        }
      } else if (typeof question.options === 'object') {
        // For object format, swap the values
        const newOptions = { ...question.options };
        const currentText = newOptions[currentCorrectLetter];
        const newText = newOptions[newAnswerLetter];
        if (currentText !== undefined && newText !== undefined) {
          newOptions[currentCorrectLetter] = newText;
          newOptions[newAnswerLetter] = currentText;
          question.options = newOptions;
        }
      }
    }
    
    // Update correct answer
    question.correctAnswer = newAnswerLetter;
    updated[questionIndex] = question;
    setQuestions(updated);

    // Mark as edited in metadata
    setMetadataMap((prev) => ({
      ...prev,
      [questionIndex]: {
        ...(prev[questionIndex] || {}),
        mode: "edited",
      },
    }));
  };

  const handleExport = () => {
    const accepted = questions
      .map((q, i) => ({ q, i }))
      .filter(({ i }) => metadataMap[i]?.status === "accepted");

    if (accepted.length === 0) {
      setShowEmptyExportModal(true);
    } else {
      setShowFileTypeModal(true);
    }
  };

  // Export as TXT file
  const exportAsTxt = async (accepted) => {
    const txtContent = accepted
      .map(({ q }, idx) => {
        // Robustly handle options and correctAnswer
        let optionsText = "";
        if (Array.isArray(q.options)) {
          optionsText = q.options.join("\n");
        } else if (q.options && typeof q.options === "object") {
          optionsText = Object.entries(q.options)
            .map(([key, value]) => `${key}) ${value}`)
            .join("\n");
        } else {
          optionsText = "";
        }
        const formattedAnswer = getFormattedCorrectAnswer(q);
        return `${idx + 1}. ${q.question}\n${optionsText}\nCorrect Answer: ${formattedAnswer}\n`;
      })
      .join("\n");
    
    try {
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${outputFileName}.txt`,
          types: [
            {
              description: "Text File",
              accept: { "text/plain": [".txt"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(txtContent);
        await writable.close();
      } else {
        const blob = new Blob([txtContent], { type: "text/plain" });
        saveAs(blob, `${outputFileName}.txt`);
      }
      return true;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error saving TXT file:", err);
        alert("Failed to save TXT file. See console for details.");
      }
      return false;
    }
  };

  // Export as QTI package
  const exportAsQTI = async (accepted) => {
    // Build QTI-style question objects
    const qtiQuestions = accepted.map(({ q }, idx) => {
      let optionsObj = {};

      if (Array.isArray(q.options)) {
        // Convert array -> { A: "...", B: "...", ... }
        q.options.forEach((opt, optIdx) => {
          const ident = String.fromCharCode(65 + optIdx); // A, B, C...
          optionsObj[ident] = opt;
        });
      } else if (q.options && typeof q.options === "object") {
        // Assume already like { A: "...", B: "..." }
        optionsObj = { ...q.options };
      }

      const answerIdent = getAnswerIdent(q.options, q.correctAnswer);

      return {
        id: idx + 1,
        question: q.question,
        options: optionsObj,
        answer: answerIdent,
      };
    });

    // Filter out any questions where we couldn't determine an answer ident
    const validQtiQuestions = qtiQuestions.filter((q) => q.answer);

    if (!validQtiQuestions.length) {
      alert(
        "No valid questions with a determinable correct answer. Check correctAnswer values."
      );
      return false;
    }

    const qtiFileName = `${outputFileName}.xml`;
    const questionBankName = "DeepToast Question Bank";
    const xml = generateQtiXml(validQtiQuestions);
    const manifest = generateManifest(qtiFileName, questionBankName);

    const zip = new JSZip();
    zip.file(qtiFileName, xml);
    zip.file("imsmanifest.xml", manifest);

    try {
      const blob = await zip.generateAsync({ type: "blob" });

      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${outputFileName}_qti_package.zip`,
          types: [
            {
              description: "QTI Package",
              accept: { "application/zip": [".zip"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        saveAs(blob, `${outputFileName}_qti_package.zip`);
      }
      return true;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error saving QTI ZIP:", err);
        alert("Failed to save QTI package. See console for details.");
      }
      return false;
    }
  };

  // Export as PDF (without correct answers)
  const exportAsPDFTest = async (accepted) => {
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const lineHeight = 7;
      const questionSpacing = 15;

      accepted.forEach(({ q }, idx) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }

        // Question number and text
        const questionText = `${idx + 1}. ${q.question}`;
        const splitQuestion = doc.splitTextToSize(questionText, doc.internal.pageSize.width - 2 * margin);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(splitQuestion, margin, yPos);
        yPos += splitQuestion.length * lineHeight + 5;

        // Options
        doc.setFont(undefined, 'normal');
        let optionsText = "";
        if (Array.isArray(q.options)) {
          optionsText = q.options.join("\n");
        } else if (q.options && typeof q.options === "object") {
          optionsText = Object.entries(q.options)
            .map(([key, value]) => `${key}) ${value}`)
            .join("\n");
        }

        if (optionsText) {
          const splitOptions = doc.splitTextToSize(optionsText, doc.internal.pageSize.width - 2 * margin);
          doc.text(splitOptions, margin + 10, yPos);
          yPos += splitOptions.length * lineHeight;
        }

        yPos += questionSpacing;
      });

      const blob = doc.output('blob');
      
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${outputFileName}_test.pdf`,
          types: [
            {
              description: "PDF File",
              accept: { "application/pdf": [".pdf"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        saveAs(blob, `${outputFileName}_test.pdf`);
      }
      return true;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error saving PDF test file:", err);
        alert("Failed to save PDF test file. See console for details.");
      }
      return false;
    }
  };

  // Export as PDF (with answer key)
  const exportAsPDFAnswerKey = async (accepted) => {
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const lineHeight = 7;
      const questionSpacing = 15;

      accepted.forEach(({ q }, idx) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }

        // Question number and text
        const questionText = `${idx + 1}. ${q.question}`;
        const splitQuestion = doc.splitTextToSize(questionText, doc.internal.pageSize.width - 2 * margin);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(splitQuestion, margin, yPos);
        yPos += splitQuestion.length * lineHeight + 5;

        // Options
        doc.setFont(undefined, 'normal');
        let optionsText = "";
        if (Array.isArray(q.options)) {
          optionsText = q.options.join("\n");
        } else if (q.options && typeof q.options === "object") {
          optionsText = Object.entries(q.options)
            .map(([key, value]) => `${key}) ${value}`)
            .join("\n");
        }

        if (optionsText) {
          const splitOptions = doc.splitTextToSize(optionsText, doc.internal.pageSize.width - 2 * margin);
          doc.text(splitOptions, margin + 10, yPos);
          yPos += splitOptions.length * lineHeight;
        }

        // Correct Answer
        const formattedAnswer = getFormattedCorrectAnswer(q);
        if (formattedAnswer) {
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 128, 0); // Green color
          doc.text(`Correct Answer: ${formattedAnswer}`, margin + 10, yPos);
          doc.setTextColor(0, 0, 0); // Reset to black
          yPos += lineHeight + 5;
        }

        yPos += questionSpacing;
      });

      const blob = doc.output('blob');
      
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${outputFileName}_answer_key.pdf`,
          types: [
            {
              description: "PDF File",
              accept: { "application/pdf": [".pdf"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        saveAs(blob, `${outputFileName}_answer_key.pdf`);
      }
      return true;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error saving PDF answer key file:", err);
        alert("Failed to save PDF answer key file. See console for details.");
      }
      return false;
    }
  };

  // Generate PDF blob (without answers) - helper function
  const generatePDFTestBlob = (accepted) => {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;
    const questionSpacing = 15;

    accepted.forEach(({ q }, idx) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      const questionText = `${idx + 1}. ${q.question}`;
      const splitQuestion = doc.splitTextToSize(questionText, doc.internal.pageSize.width - 2 * margin);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(splitQuestion, margin, yPos);
      yPos += splitQuestion.length * lineHeight + 5;

      doc.setFont(undefined, 'normal');
      let optionsText = "";
      if (Array.isArray(q.options)) {
        optionsText = q.options.join("\n");
      } else if (q.options && typeof q.options === "object") {
        optionsText = Object.entries(q.options)
          .map(([key, value]) => `${key}) ${value}`)
          .join("\n");
      }

      if (optionsText) {
        const splitOptions = doc.splitTextToSize(optionsText, doc.internal.pageSize.width - 2 * margin);
        doc.text(splitOptions, margin + 10, yPos);
        yPos += splitOptions.length * lineHeight;
      }

      yPos += questionSpacing;
    });

    return doc.output('blob');
  };

  // Generate PDF blob (with answer key) - helper function
  const generatePDFAnswerKeyBlob = (accepted) => {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;
    const questionSpacing = 15;

    accepted.forEach(({ q }, idx) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      const questionText = `${idx + 1}. ${q.question}`;
      const splitQuestion = doc.splitTextToSize(questionText, doc.internal.pageSize.width - 2 * margin);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(splitQuestion, margin, yPos);
      yPos += splitQuestion.length * lineHeight + 5;

      doc.setFont(undefined, 'normal');
      let optionsText = "";
      if (Array.isArray(q.options)) {
        optionsText = q.options.join("\n");
      } else if (q.options && typeof q.options === "object") {
        optionsText = Object.entries(q.options)
          .map(([key, value]) => `${key}) ${value}`)
          .join("\n");
      }

      if (optionsText) {
        const splitOptions = doc.splitTextToSize(optionsText, doc.internal.pageSize.width - 2 * margin);
        doc.text(splitOptions, margin + 10, yPos);
        yPos += splitOptions.length * lineHeight;
      }

      const formattedAnswer = getFormattedCorrectAnswer(q);
      if (formattedAnswer) {
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 128, 0);
        doc.text(`Correct Answer: ${formattedAnswer}`, margin + 10, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += lineHeight + 5;
      }

      yPos += questionSpacing;
    });

    return doc.output('blob');
  };

  // Generate QTI ZIP blob - helper function
  const generateQTIZipBlob = async (accepted) => {
    const qtiQuestions = accepted.map(({ q }, idx) => {
      let optionsObj = {};

      if (Array.isArray(q.options)) {
        q.options.forEach((opt, optIdx) => {
          const ident = String.fromCharCode(65 + optIdx);
          optionsObj[ident] = opt;
        });
      } else if (q.options && typeof q.options === "object") {
        optionsObj = { ...q.options };
      }

      const answerIdent = getAnswerIdent(q.options, q.correctAnswer);

      return {
        id: idx + 1,
        question: q.question,
        options: optionsObj,
        answer: answerIdent,
      };
    });

    const validQtiQuestions = qtiQuestions.filter((q) => q.answer);

    if (!validQtiQuestions.length) {
      throw new Error("No valid questions with a determinable correct answer.");
    }

    const qtiFileName = `${outputFileName}.xml`;
    const questionBankName = "DeepToast Question Bank";
    const xml = generateQtiXml(validQtiQuestions);
    const manifest = generateManifest(qtiFileName, questionBankName);

    const zip = new JSZip();
    zip.file(qtiFileName, xml);
    zip.file("imsmanifest.xml", manifest);

    return await zip.generateAsync({ type: "blob" });
  };

  // Handle file type selection and export (saves all selected files separately)
  const handleFileTypeExport = async () => {
    const accepted = questions
      .map((q, i) => ({ q, i }))
      .filter(({ i }) => metadataMap[i]?.status === "accepted");

    if (!accepted.length) {
      alert("No accepted questions to export.");
      return;
    }

    // Check if at least one file type is selected
    const selectedCount = Object.values(selectedFileTypes).filter(Boolean).length;
    if (selectedCount === 0) {
      alert("Please select at least one file type to export.");
      return;
    }

    setShowFileTypeModal(false);

    try {
      const exportResults = [];

      // Save each selected file type separately
      if (selectedFileTypes.pdfTest) {
        const blob = generatePDFTestBlob(accepted);
        try {
          if (window.showSaveFilePicker) {
            const handle = await window.showSaveFilePicker({
              suggestedName: `${outputFileName}_test.pdf`,
              types: [{ description: "PDF File", accept: { "application/pdf": [".pdf"] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            exportResults.push(true);
          } else {
            saveAs(blob, `${outputFileName}_test.pdf`);
            exportResults.push(true);
          }
        } catch (err) {
          if (err.name !== "AbortError") {
            console.error("Error saving PDF test file:", err);
            exportResults.push(false);
          }
        }
      }

      if (selectedFileTypes.pdfAnswerKey) {
        const blob = generatePDFAnswerKeyBlob(accepted);
        try {
          if (window.showSaveFilePicker) {
            const handle = await window.showSaveFilePicker({
              suggestedName: `${outputFileName}_answer_key.pdf`,
              types: [{ description: "PDF File", accept: { "application/pdf": [".pdf"] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            exportResults.push(true);
          } else {
            saveAs(blob, `${outputFileName}_answer_key.pdf`);
            exportResults.push(true);
          }
        } catch (err) {
          if (err.name !== "AbortError") {
            console.error("Error saving PDF answer key file:", err);
            exportResults.push(false);
          }
        }
      }

      if (selectedFileTypes.qti) {
        const blob = await generateQTIZipBlob(accepted);
        try {
          if (window.showSaveFilePicker) {
            const handle = await window.showSaveFilePicker({
              suggestedName: `${outputFileName}_qti_package.zip`,
              types: [{ description: "QTI Package", accept: { "application/zip": [".zip"] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            exportResults.push(true);
          } else {
            saveAs(blob, `${outputFileName}_qti_package.zip`);
            exportResults.push(true);
          }
        } catch (err) {
          if (err.name !== "AbortError") {
            console.error("Error saving QTI package:", err);
            exportResults.push(false);
          }
        }
      }

      // Reset checkboxes
      setSelectedFileTypes({
        pdfTest: false,
        pdfAnswerKey: false,
        qti: false
      });

      // Show success modal if at least one export succeeded
      if (exportResults.some(result => result)) {
        setShowModal(true);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error exporting files:", err);
        alert(`Failed to export files: ${err.message}`);
      }
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (fileType) => {
    setSelectedFileTypes(prev => ({
      ...prev,
      [fileType]: !prev[fileType]
    }));
  };

  const exportToCanvasDirect = async () => {
    const accepted = questions
      .map((q, i) => ({ q, i }))
      .filter(({ i }) => metadataMap[i]?.status === "accepted");

    if (!accepted.length) {
      setCanvasUploadError("No accepted questions to export.");
      return;
    }

    setCanvasUploadError("");
    setCanvasUploading(true);
    setCanvasUploadSuccess(false);

    try {
      const zipBlob = await generateQTIZipBlob(accepted);
      const file = new File([zipBlob], `${outputFileName}_qti_package.zip`, { type: "application/zip" });

      const fd = new FormData();
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes.data?.session?.user?.id || "";
      if (userId) {
        fd.append("userId", userId);
      }
      fd.append("courseId", canvasCourseId.trim());
      fd.append("questionBankName", canvasQuestionBankName.trim() || "DeepToast Question Bank");
      fd.append("file", file);

      const resp = await fetch(apiUrl("/api/canvas/import-qti"), {
        method: "POST",
        body: fd,
      });

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json?.error || "Canvas import failed");
      }

      setCanvasUploadSuccess(true);
    } catch (e) {
      setCanvasUploadError(e.message || "Canvas import failed.");
    } finally {
      setCanvasUploading(false);
    }
  };

  // Remove generateQuestionsStream and all streaming/EventSource logic

  const getCorrectAnswerText = (q) => {
    if (!q) return "";
    const letter = typeof q.correctAnswer === "string" ? q.correctAnswer.trim() : "";
    // MCQ object options { A: text, B: text, ... }
    if (q.options && !Array.isArray(q.options) && typeof q.options === 'object') {
      if (letter && q.options[letter]) return q.options[letter];
    }
    // Array options like ["A) True", "B) False"] or ["A) foo", ...]
    if (Array.isArray(q.options) && letter) {
      const match = q.options.find(opt => typeof opt === 'string' && opt.trim().toUpperCase().startsWith((letter.toUpperCase() + ")")));
      if (match) return match.replace(/^([A-D])\)\s*/i, '').trim();
    }
    // Boolean styles
    if (typeof q.correctAnswer === 'boolean') return q.correctAnswer ? 'True' : 'False';
    if (typeof q.answer === 'boolean') return q.answer ? 'True' : 'False';
    if (typeof q.answer === 'string' && !letter) return q.answer;
    return letter || '';
  };

  // Get formatted correct answer with option letter and text
  const getFormattedCorrectAnswer = (q) => {
    if (!q) return "";
    
    // Handle boolean cases first
    if (typeof q.correctAnswer === 'boolean' || typeof q.answer === 'boolean') {
      const boolValue = q.correctAnswer !== undefined ? q.correctAnswer : q.answer;
      const letter = boolValue ? 'A' : 'B'; // True = A, False = B (assuming TF questions show True first)
      return `${letter}) ${boolValue ? 'True' : 'False'}`;
    }
    
    // Get the option letter (A, B, C, D) using the existing getAnswerIdent function
    const correctAnswerValue = q.correctAnswer !== undefined ? q.correctAnswer : q.answer;
    const answerLetter = getAnswerIdent(q.options, correctAnswerValue);
    
    // Get the text (this function handles various formats)
    const answerText = getCorrectAnswerText(q);
    
    // If we have both letter and text
    if (answerLetter && answerText) {
      // Check if the text already contains the letter format (e.g., "A) True")
      if (answerText.match(/^[A-D]\)\s*/i)) {
        return answerText;
      }
      // Format as "A) Text"
      return `${answerLetter}) ${answerText}`;
    }
    
    // If we only have letter, try to find the corresponding text from options
    if (answerLetter && !answerText) {
      if (q.options) {
        if (Array.isArray(q.options)) {
          const match = q.options.find(opt => {
            if (typeof opt === 'string') {
              return opt.trim().toUpperCase().startsWith(answerLetter.toUpperCase() + ")");
            }
            return false;
          });
          if (match) return match.trim();
        } else if (typeof q.options === 'object' && q.options[answerLetter]) {
          return `${answerLetter}) ${q.options[answerLetter]}`;
        }
      }
      return answerLetter;
    }
    
    // If we only have text (but no letter), check if it's already formatted
    if (answerText && !answerLetter) {
      // If text already has letter format, return as is
      if (answerText.match(/^[A-D]\)\s*/i)) {
        return answerText;
      }
      // Otherwise, try to determine letter from the options
      const fallbackLetter = getAnswerIdent(q.options, answerText);
      if (fallbackLetter) {
        return `${fallbackLetter}) ${answerText}`;
      }
      return answerText;
    }
    
    return "";
  };

  return (
    <div className="min-h-screen bg-secondary">
      <AppHeader titleSize="text-5xl" />

      <div className="bg-accent text-center py-4">
        <h2 className="text-xl font-bold text-primary underline">
          Questions Generated (Number of questions – {numQuestions})
        </h2>
      </div>

      <div className="flex justify-between items-center px-10 mt-6">
        <h2 className="text-xl font-semibold text-gray-800"> Questions:</h2>
        <div className="flex flex-col gap-3 items-end">
          <button
            onClick={handleExport}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            EXPORT accepted questions or EXIT DeepToast
          </button>
          <button
            onClick={handleAcceptAllClick}
            className="bg-yellow-800 text-white px-4 py-2 rounded hover:bg-yellow-900"
          >
            Accept All Questions
          </button>
        </div>
      </div>
      {error && <div className="text-red-600 px-10">{error}</div>}
      
      <div className="px-10 mt-4 mb-2">
        <p className="text-sm text-gray-700 font-bold underline italic">
          * Click on any option to change the postion of the correct answer. The arrow will move the correct answer to that position.
        </p>
      </div>

      {generationWarnings.length > 0 && (
        <div className="px-10 mt-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <p className="font-semibold text-yellow-800 mb-2">Some questions could not be generated:</p>
            <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1">
              {generationWarnings.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
            <button
              className="mt-2 text-xs underline text-yellow-700 hover:text-yellow-900"
              onClick={() => {
                localStorage.removeItem('generationWarnings');
                setGenerationWarnings([]);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {questions.length === 0 && (
        <div className="px-10 py-8 text-lg text-gray-700">No questions found. Please generate questions from the Summary page.</div>
      )}

      <div className="px-10 pb-20 space-y-6 mt-4">
        {questions.map((q, i) => {
          const isEditing = editingIndex === i;
          const meta = metadataMap[i] || {};
          const cardBg =
            meta.status === "accepted"
              ? "bg-green-100"
              : meta.status === "rejected"
              ? "bg-red-100"
              : "bg-white";

          return (
            <div key={i} className={`${cardBg} rounded-lg shadow p-6 space-y-3`}>
              {isEditing ? (
                <>
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="w-full p-2 border rounded"
                    rows={3}
                  />
                  {editedOptions.length > 0 && (
                    <div className="space-y-2 pl-4">
                      {editedOptions.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="font-semibold text-gray-700 w-6">
                            {opt.letter})
                          </span>
                          <input
                            value={opt.text || ''}
                            onChange={(e) => handleOptionChange(idx, e.target.value)}
                            className="flex-1 p-1 border rounded"
                            placeholder="Enter option text"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-start gap-2 flex-wrap">
                    <p className="font-semibold text-lg flex-1 min-w-0">
                      Q{i + 1}. {q.question}
                    </p>
                    {q.difficulty && (
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wide whitespace-nowrap ${
                          q.difficulty === 'hard'
                            ? 'bg-red-200 text-red-800'
                            : q.difficulty === 'medium'
                            ? 'bg-yellow-200 text-yellow-800'
                            : 'bg-green-200 text-green-800'
                        }`}
                        title={q.difficulty === 'hard' ? 'Generated by Claude (Bedrock)' : 'Generated by Gemini'}
                      >
                        {q.difficulty}
                      </span>
                    )}
                  </div>
                  {q.options && (
                    <div className="space-y-1 pl-4">
                      {Array.isArray(q.options) ? (
                        q.options.map((opt, idx) => {
                          const optionLetter = String.fromCharCode(65 + idx);
                          const correctAnswerValue = q.correctAnswer !== undefined ? q.correctAnswer : q.answer;
                          const currentCorrectLetter = getAnswerIdent(q.options, correctAnswerValue);
                          const isCorrect = optionLetter === currentCorrectLetter;
                          return (
                            <div 
                              key={idx} 
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                              onClick={() => handleCorrectAnswerChange(i, optionLetter)}
                            >
                              <span className="text-green-600 font-black text-2xl w-7 text-center">
                                {isCorrect ? '→' : ''}
                              </span>
                              <span className={isCorrect ? 'font-bold' : ''}>{opt}</span>
                            </div>
                          );
                        })
                      ) : (
                        Object.entries(q.options)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([key, value]) => {
                            const correctAnswerValue = q.correctAnswer !== undefined ? q.correctAnswer : q.answer;
                            const currentCorrectLetter = getAnswerIdent(q.options, correctAnswerValue);
                            const isCorrect = key === currentCorrectLetter;
                            return (
                              <div 
                                key={key} 
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                                onClick={() => handleCorrectAnswerChange(i, key)}
                              >
                                <span className="text-green-600 font-black text-2xl w-7 text-center">
                                  {isCorrect ? '→' : ''}
                                </span>
                                <span className={isCorrect ? 'font-bold' : ''}>{key}) {value}</span>
                              </div>
                            );
                          })
                      )}
                    </div>
                  )}
                  {/* Always show options for TF questions */}
                  {q.type === "tf" && (!q.options || q.options.length === 0) && (
                    <div className="space-y-1 pl-4">
                      {(() => {
                        const correctAnswerValue = q.correctAnswer !== undefined ? q.correctAnswer : q.answer;
                        let currentCorrectLetter = '';
                        if (typeof correctAnswerValue === 'boolean') {
                          currentCorrectLetter = correctAnswerValue ? 'A' : 'B';
                        } else {
                          currentCorrectLetter = getAnswerIdent({ A: 'True', B: 'False' }, correctAnswerValue);
                        }
                        return (
                          <>
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                              onClick={() => handleCorrectAnswerChange(i, 'A')}
                            >
                              <span className="text-green-600 font-black text-2xl w-7 text-center">
                                {currentCorrectLetter === 'A' ? '→' : ''}
                              </span>
                              <span className={currentCorrectLetter === 'A' ? 'font-bold' : ''}>A) True</span>
                            </div>
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                              onClick={() => handleCorrectAnswerChange(i, 'B')}
                            >
                              <span className="text-green-600 font-black text-2xl w-7 text-center">
                                {currentCorrectLetter === 'B' ? '→' : ''}
                              </span>
                              <span className={currentCorrectLetter === 'B' ? 'font-bold' : ''}>B) False</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
              {/* Correct Answer display logic */}
              {(() => {
                const formattedAnswer = getFormattedCorrectAnswer(q);
                return formattedAnswer ? (
                  <p className="mt-2 font-medium text-green-700">
                    Correct Answer: {formattedAnswer}
                  </p>
                ) : null;
              })()}
              {meta.status && (
                <p className="text-sm text-gray-500">
                  Status: <strong>{meta.status}</strong> | Mode: <strong>{meta.mode}</strong>
                </p>
              )}
              <div className="flex gap-2 justify-end">
                {isEditing ? (
                  <>
                    <button
                      className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
                      onClick={handleSave}
                    >
                      Save
                    </button>
                    <button
                      className="bg-gray-400 text-white px-4 py-1 rounded hover:bg-gray-500"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="bg-red-700 text-white px-4 py-1 rounded hover:bg-red-600"
                      onClick={() => handleStatusChange(i, "rejected")}
                    >
                      Reject
                    </button>
                    <button
                      className="bg-yellow-400 text-black px-4 py-1 rounded hover:bg-yellow-500"
                      onClick={() => handleEdit(i)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-green-700 text-white px-4 py-1 rounded hover:bg-green-600"
                      onClick={() => handleStatusChange(i, "accepted")}
                    >
                      Accept
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        <div className="text-right mt-10">
          <button
            onClick={handleExport}
            className="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            EXPORT accepted questions or EXIT DeepToast
          </button>
        </div>
      </div>

      {/* ✅ Post-export modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="relative bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md space-y-4 text-center">
            <button
              className="absolute top-3 right-5 text-gray-600 hover:text-gray-900 text-lg font-bold"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-xl font-semibold text-green-700">Export Successful!</h3>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  navigate("/canvas-instructions");
                }}
                className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
              >
                Directions to import Questions to Canvas
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
              >
                Generate More Questions
              </button>
              <button
                onClick={() => navigate("/exit")}
                className="bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
              >
                Exit DeepToast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ❌ Empty export modal */}
      {showEmptyExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md text-center space-y-4">
            <h3 className="text-lg font-semibold text-red-600">You have not accepted any questions!</h3>
            <div className="flex flex-col gap-3">
              <button
                className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
                onClick={() => setShowEmptyExportModal(false)}
              >
                Go back to review questions
              </button>
              <button
                className="bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
                onClick={() => {
                  setConfirmAction("generate");
                  setShowEmptyExportModal(false);
                  setShowConfirmDialog(true);
                }}
              >
                Generate more questions
              </button>
              <button
                className="bg-gray-700 text-white py-2 rounded hover:bg-gray-800"
                onClick={() => {
                  setConfirmAction("exit");
                  setShowEmptyExportModal(false);
                  setShowConfirmDialog(true);
                }}
              >
                Exit DeepToast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ❓ Confirmation dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md text-center space-y-4">
            <h3 className="text-lg font-semibold text-black-600">
              You will lose currently generated questions. Do you still want to {confirmAction === "generate" ? "proceed" : "exit"}?
            </h3>
            <div className="flex gap-4 justify-center">
              <button
                className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
                onClick={() => {
                  setShowConfirmDialog(false);
                  if (confirmAction === "generate") {
                    navigate("/");
                  } else {
                    navigate("/exit");
                  }
                }}
              >
                Yes
              </button>
              <button
                className="bg-gray-400 text-white py-2 px-4 rounded hover:bg-gray-500"
                onClick={() => setShowConfirmDialog(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📄 File type selection modal */}
      {showFileTypeModal && (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
        <div className="relative bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md space-y-4">

          {/* Close button */}
          <button
            className="absolute top-3 right-5 text-gray-600 hover:text-gray-900 text-lg font-bold"
            onClick={() => {
              setShowFileTypeModal(false);
              setSelectedFileTypes({
                pdfTest: false,
                pdfAnswerKey: false,
                qti: false,
              });
              setCanvasUploadError("");
              setCanvasUploadSuccess(false);
            }}
          >
            &times;
          </button>

          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-800 text-center">
            Select File Type
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 text-center leading-relaxed">
            To import these questions into Canvas, you will need to create a QTI package file.
            Select one or more file types to export. All selected files will be saved separately.
          </p>

          {/* CHECKBOXES */}
          <div className="flex flex-col gap-3 mt-4">
            <label className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFileTypes.pdfTest}
                onChange={() => handleCheckboxChange("pdfTest")}
                className="w-5 h-5"
              />
              <span className="text-sm font-medium text-gray-700">
                Create PDF file of a usable test
              </span>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFileTypes.pdfAnswerKey}
                onChange={() => handleCheckboxChange("pdfAnswerKey")}
                className="w-5 h-5"
              />
              <span className="text-sm font-medium text-gray-700">
                Create PDF file with answer key
              </span>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFileTypes.qti}
                onChange={() => handleCheckboxChange("qti")}
                className="w-5 h-5"
              />
              <span className="text-sm font-medium text-gray-700">
                Create QTI package file
              </span>
            </label>
          </div>

          {/* Export Selected Files */}
          <button
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 font-semibold transition"
            onClick={handleFileTypeExport}
          >
            Export Selected Files
          </button>

          {/* OR divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="text-gray-500 text-sm font-semibold">OR</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          {/* Canvas Section */}
          <div className="mt-5 space-y-3 border-t pt-4">
            <h4 className="text-base font-semibold text-gray-800">
              Export directly to Canvas
            </h4>

            <p className="text-sm text-gray-700">
              Course used for question generation:{" "}
              <span className="font-semibold">{canvasCourseName || canvasCourseId || "N/A"}</span>
            </p>

            {canvasUploadError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                {typeof canvasUploadError === "string"
                  ? canvasUploadError
                  : "Export failed. Please check your Canvas token/URL and try again."}
              </p>
            )}

            {/* Success message */}
            {canvasUploadSuccess && (
              <p className="text-sm text-green-700">
                Uploaded to Canvas successfully.
              </p>
            )}

            <button
              className="w-full bg-green-500 text-white py-2.5 rounded-lg hover:bg-green-600 font-semibold transition disabled:opacity-60"
              onClick={async () => {
                setCanvasUploadError("");
                setCanvasUploadSuccess(false);

                try {
                  await exportToCanvasDirect();
                } catch (e) {
                  console.error("Canvas export error:", e);
                  setCanvasUploadError(e?.message || "Canvas export failed.");
                }
              }}
              disabled={canvasUploading || !canvasCourseId.trim()}
            >
              {canvasUploading ? "Exporting..." : "Export to Canvas"}
            </button>
          </div>

        </div>
      </div>
    )}

      {/* ✅ Confirm export modal */}
      {showConfirmExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md text-center space-y-4">
            <div className="flex flex-col gap-3">
              <button
                className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
                onClick={async () => {
                  const accepted = questions
                    .map((q, i) => ({ q, i }))
                    .filter(({ i }) => metadataMap[i]?.status === "accepted");

                  if (!accepted.length) {
                    alert("No accepted questions to export.");
                    return;
                  }

                  // Build QTI-style question objects
                  const qtiQuestions = accepted.map(({ q }, idx) => {
                    let optionsObj = {};

                    if (Array.isArray(q.options)) {
                      // Convert array -> { A: "...", B: "...", ... }
                      q.options.forEach((opt, optIdx) => {
                        const ident = String.fromCharCode(65 + optIdx); // A, B, C...
                        optionsObj[ident] = opt;
                      });
                    } else if (q.options && typeof q.options === "object") {
                      // Assume already like { A: "...", B: "..." }
                      optionsObj = { ...q.options };
                    }

                    const answerIdent = getAnswerIdent(q.options, q.correctAnswer);

                    return {
                      id: idx + 1,
                      question: q.question,
                      options: optionsObj,
                      answer: answerIdent,
                    };
                  });

                  // Filter out any questions where we couldn't determine an answer ident
                  const validQtiQuestions = qtiQuestions.filter((q) => q.answer);

                  if (!validQtiQuestions.length) {
                    alert(
                      "No valid questions with a determinable correct answer. Check correctAnswer values."
                    );
                    return;
                  }

                  const qtiFileName = `${outputFileName}.xml`;
                  const questionBankName = "DeepToast Question Bank";
                  const xml = generateQtiXml(validQtiQuestions);
                  const manifest = generateManifest(qtiFileName, questionBankName);

                  const zip = new JSZip();
                  zip.file(qtiFileName, xml);
                  zip.file("imsmanifest.xml", manifest);

                  try {
                    const blob = await zip.generateAsync({ type: "blob" });

                    if (window.showSaveFilePicker) {
                      const handle = await window.showSaveFilePicker({
                        suggestedName: `${outputFileName}_qti_package.zip`,
                        types: [
                          {
                            description: "QTI Package",
                            accept: { "application/zip": [".zip"] },
                          },
                        ],
                      });
                      const writable = await handle.createWritable();
                      await writable.write(blob);
                      await writable.close();
                    } else {
                      saveAs(blob, `${outputFileName}_qti_package.zip`);
                    }

                    setShowConfirmExportModal(false);
                    setShowModal(true);
                  } catch (err) {
                    if (err.name !== "AbortError") {
                      console.error("Error saving QTI ZIP:", err);
                      alert("Failed to save QTI package. See console for details.");
                    }
                  }
                }}
              >
                Export questions now
              </button>

              {/* <button
                className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
                onClick={async () => {
                  const accepted = questions
                    .map((q, i) => ({ q, i }))
                    .filter(({ i }) => metadataMap[i]?.status === "accepted");
                  const txtContent = accepted
                    .map(({ q }, idx) => {
                      // Robustly handle options and correctAnswer
                      let optionsText = "";
                      if (Array.isArray(q.options)) {
                        optionsText = q.options.join("\n");
                      } else if (q.options && typeof q.options === "object") {
                        optionsText = Object.entries(q.options)
                          .map(([key, value]) => `${key}) ${value}`)
                          .join("\n");
                      } else {
                        optionsText = "";
                      }
                      const correct = q.correctAnswer !== undefined && q.correctAnswer !== null ? q.correctAnswer : "";
                      return `${idx + 1}. ${q.question}\n${optionsText}\nCorrect Answer: ${correct}\n`;
                    })
                    .join("\n");
                  try {
                    if (window.showSaveFilePicker) {
                      const handle = await window.showSaveFilePicker({
                        suggestedName: "accepted_questions.txt",
                        types: [
                          {
                            description: "Text File",
                            accept: { "text/plain": [".txt"] },
                          },
                        ],
                      });
                      const writable = await handle.createWritable();
                      await writable.write(txtContent);
                      await writable.close();
                    } else {
                      const blob = new Blob([txtContent], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = "accepted_questions.txt";
                      link.click();
                      URL.revokeObjectURL(url);
                    }
                    setShowConfirmExportModal(false);
                    setShowModal(true);
                  } catch (err) {
                    if (err.name !== "AbortError") {
                      console.error("Error saving file:", err);
                    }
                  }
                }}
              >
                I confirm I want to export questions
              </button> */}
              <h3 className="text-lg font-semibold text-black">OR</h3>
              <button
                className="bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
                onClick={() => {
                  setShowConfirmExportModal(false);
                  navigate("/exit");
                }}
              >
                Exit DeepToast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Accept All Questions Confirmation Modal */}
      {showAcceptAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md text-center space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              You are about to accept all questions. This may result in inappropriate questions in the question bank. Proceed with caution.
            </h3>
            <div className="flex gap-4 justify-center">
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                onClick={handleAcceptAll}
              >
                Proceed
              </button>
              <button
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                onClick={() => setShowAcceptAllModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateQuestion;
